import { Formik } from 'formik';
import React, { useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Field, Form, FormMessage, ModalCard, Radio, RadioGroup } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';

const Description = styled.p`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

export default function SageSetupModal({ onClose, onFinish }) {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const [step, setStep] = useState('credentials');
  const [credentials, setCredentials] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntityKey, setSelectedEntityKey] = useState(null);

  const initialValues = {
    sageCompanyId: '',
    sageUserId: '',
  };

  const resolveError = ({ message }) =>
    message ?? 'There was a problem completing your request. Please try again later.';

  const sage = () => api.www.workspaces(workspace.id).sage();

  async function handleSubmitCredentials(values) {
    setIsLoading(true);
    setErrorMessage();
    const sageCompanyId = values.sageCompanyId.trim();
    const sageUserId = values.sageUserId.trim();
    try {
      const { data } = await sage().inspect({ sageCompanyId, sageUserId });
      if (!isMounted.current) return;
      setCredentials({ sageCompanyId, sageUserId });
      setEntities(data.entities ?? []);
      setStep('entity');
    } catch (error) {
      if (!isMounted.current) return;
      setErrorMessage(resolveError(error));
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }

  async function handleConnectEntity() {
    if (!selectedEntityKey) return;
    setIsLoading(true);
    setErrorMessage();
    try {
      const { data } = await sage().connect({ ...credentials, entityKey: selectedEntityKey });
      if (!isMounted.current) return;
      onFinish(data.connectionId);
    } catch (error) {
      if (!isMounted.current) return;
      setErrorMessage(resolveError(error));
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }

  if (step === 'entity') {
    return (
      <ModalCard title="Select a Sage Intacct Entity" onClose={onClose}>
        <ModalCard.Body>
          <Description>Select the Sage Intacct entity to connect to this workspace.</Description>
          <RadioGroup direction="vertical" value={selectedEntityKey} onChange={(value) => setSelectedEntityKey(value)}>
            {entities.map((entity) => (
              <Radio
                key={entity.key}
                value={entity.key}
                disabled={entity.taken}
                label={`${entity.id} - ${entity.name}${entity.taken ? ' (already connected)' : ''}`}
              />
            ))}
          </RadioGroup>
          {entities.length === 0 && <Description>No entities are available to connect.</Description>}
          {errorMessage && <FormMessage.Error>{errorMessage}</FormMessage.Error>}
        </ModalCard.Body>
        <ModalCard.Footer>
          <Buttons align="right">
            <CancelButton onClick={onClose} style={{ marginRight: 'auto' }}>
              Close
            </CancelButton>
            <Button isLoading={isLoading} disabled={!selectedEntityKey} onClick={handleConnectEntity}>
              Connect
            </Button>
          </Buttons>
        </ModalCard.Footer>
      </ModalCard>
    );
  }

  return (
    <ModalCard title="Setup Sage Intacct Integration" onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmitCredentials}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          sageCompanyId: Yup.string().label('Sage Company ID').max(255).required(),
          sageUserId: Yup.string().label('Web Services User ID').max(255).required(),
        })}>
        <Form>
          <ModalCard.Body>
            <Description>
              Sage Intacct authorizes Ruddr through a Web Services User that an admin in your Sage company creates and
              grants access to the Ruddr Client ID.
            </Description>
            <Form.Control>
              <Field.Text
                name="sageCompanyId"
                label="Sage Company ID"
                placeholder="Sage Company ID"
                maxLength={255}
                autoFocus
              />
            </Form.Control>
            <Form.Control>
              <Field.Text
                name="sageUserId"
                label="Web Services User ID"
                placeholder="Web Services User ID"
                maxLength={255}
              />
            </Form.Control>

            {errorMessage && <FormMessage.Error>{errorMessage}</FormMessage.Error>}
          </ModalCard.Body>
          <ModalCard.Footer>
            <Buttons align="right">
              <CancelButton onClick={onClose} style={{ marginRight: 'auto' }}>
                Close
              </CancelButton>
              <Button type="submit" isLoading={isLoading}>
                Save
              </Button>
            </Buttons>
          </ModalCard.Footer>
        </Form>
      </Formik>
    </ModalCard>
  );
}
